import React, { useState, useRef } from 'react';
import { AnimatedButton } from '@trlogic/ui-pack';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { RadioButton } from 'primereact/radiobutton';
import { OverlayPanel } from 'primereact/overlaypanel';
import Icon from 'icons/Icon';
import RoleHelper from '../../../RoleHelper';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';
import { getComponentIcon } from '../../../util/GetComponentIcon';
import RuleCreateDropdown from '../RuleCreateDropdown';

function EmptyComponent(props: Props): React.JSX.Element {
  const {
    type,
    router: { navigate },
  } = props;
  const [list, setList] = useState('');
  const [enrichment, setEnrichment] = useState('');
  const [action, setAction] = useState('');
  const createOverlayRef = useRef<OverlayPanel | null>(null);

  const open = () => {
    if (props.onCreate) {
      props.onCreate();
      return;
    }
    switch (type) {
      case 'Connection':
        navigate('/connection/create');
        break;
      case 'Configuration':
        navigate('/configuration/create');
        break;
      case 'Rule':
        break;
      case 'Struct':
        navigate('/struct/create');
        break;
      case 'Transaction':
        navigate(`/transaction/create`);
        break;
      case 'Stream':
        navigate(`/stream/create`);
        break;
      case 'List':
        if (list == 'internalList') {
          navigate(`/list/create/file`);
        } else if (list == 'externalList') {
          navigate(`/list/create/query`);
        }
        break;
      case 'Enrichment':
        if (enrichment != '') {
          navigate(`/enrichment/${enrichment}/create`);
        }
        break;
      case 'Ai-Model':
        navigate(`/ai-model/create`);
        break;
      case 'Action':
        if (action != '') {
          navigate(`/action/${action}/create`);
        }
        break;
      case 'Tracker':
        navigate(`/tracker/create`);
        break;
    }
  };

  const navigateToCreate = (path: string) => {
    navigate(`${path}/create`);
  };

  const openCreatePage = (e: any) => {
    if (!RoleHelper.canEdit('Rule')) return;
    createOverlayRef.current?.toggle(e);
  };

  const getType = () => {
    return LanguageUtil.get({ id: `pages.${type.toLowerCase()}.title`, defaultMessage: type });
  };

  const getCreateList = () => {
    if (props.createDisabled) {
      return null;
    }
    return (
      <>
        {type == 'List' && (
          <div className='flex flex-wrap gap-3 select-none'>
            <div className='flex align-items-center items-center'>
              <RadioButton
                inputId='internalList'
                name='Internal List'
                value='internalList'
                onChange={(e) => setList(e.value)}
                checked={list == 'internalList'}
              />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.internalList', defaultMessage: 'Internal List' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton
                inputId='externalList'
                name='External List'
                value='externalList'
                onChange={(e) => setList(e.value)}
                checked={list == 'externalList'}
              />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.externalList', defaultMessage: 'External List' })}</label>
            </div>
          </div>
        )}
        {type == 'Enrichment' && (
          <div className='flex flex-wrap gap-3 select-none'>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='velocity' name='Velocity' value='velocity' onChange={(e) => setEnrichment(e.value)} checked={enrichment == 'velocity'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.velocity', defaultMessage: 'Velocity' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='list' name='List' value='list' onChange={(e) => setEnrichment(e.value)} checked={enrichment == 'list'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.list', defaultMessage: 'List' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='api' name='API' value='api' onChange={(e) => setEnrichment(e.value)} checked={enrichment == 'api'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.api', defaultMessage: 'API' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='query' name='Query' value='query' onChange={(e) => setEnrichment(e.value)} checked={enrichment == 'query'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.query', defaultMessage: 'Query' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='basic' name='Basic' value='basic' onChange={(e) => setEnrichment(e.value)} checked={enrichment == 'basic'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.basic', defaultMessage: 'Basic' })}</label>
            </div>
          </div>
        )}
        {type == 'Action' && (
          <div className='flex flex-wrap gap-3 select-none'>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='email' name='Email' value='email' onChange={(e) => setAction(e.value)} checked={action == 'email'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.email', defaultMessage: 'E-mail' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='api' name='API' value='api' onChange={(e) => setAction(e.value)} checked={action == 'api'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.api', defaultMessage: 'API' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='slack' name='Slack' value='slack' onChange={(e) => setAction(e.value)} checked={action == 'slack'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.slack', defaultMessage: 'Slack' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='discord' name='Discord' value='discord' onChange={(e) => setAction(e.value)} checked={action == 'discord'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.discord', defaultMessage: 'Discord' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton inputId='jira' name='Jira' value='jira' onChange={(e) => setAction(e.value)} checked={action == 'jira'} />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.jira', defaultMessage: 'Jira' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton
                inputId='microsoft-teams'
                name='Microsoft Teams'
                value='microsoft-teams'
                onChange={(e) => setAction(e.value)}
                checked={action == 'microsoft-teams'}
              />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.teams', defaultMessage: 'Microsoft Teams' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton
                inputId='add-to-list'
                name='Add to List'
                value='add-to-list'
                onChange={(e) => setAction(e.value)}
                checked={action == 'add-to-list'}
              />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.addToList', defaultMessage: 'Add to List' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton
                inputId='remove-from-list'
                name='Remove from List'
                value='remove-from-list'
                onChange={(e) => setAction(e.value)}
                checked={action == 'remove-from-list'}
              />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.removeFromList', defaultMessage: 'Remove From List' })}</label>
            </div>
            <div className='flex align-items-center items-center'>
              <RadioButton
                inputId='send-transaction'
                name='Send Transaction'
                value='send-transaction'
                onChange={(e) => setAction(e.value)}
                checked={action == 'send-transaction'}
              />
              <label className='ml-2'>{LanguageUtil.get({ id: 'components.emptyComponent.sendTransaction', defaultMessage: 'Send Transaction' })}</label>
            </div>
          </div>
        )}
        <section>
          {type === 'Rule' ? (
            <>
              <AnimatedButton
                disabled={!RoleHelper.canEdit(props.type)}
                icon={'add'}
                text={LanguageUtil.get({ id: 'common.button.create', defaultMessage: 'Create' })}
                customType={'primary'}
                className={'select-none'}
                onClick={(e) => RoleHelper.canEdit(props.type) && openCreatePage(e)}
              />
              <OverlayPanel ref={createOverlayRef}>
                <RuleCreateDropdown onNavigate={navigateToCreate} onDismiss={() => createOverlayRef.current?.hide()} id={type} />
              </OverlayPanel>
            </>
          ) : (
            <AnimatedButton
              disabled={!RoleHelper.canEdit(props.type)}
              icon={'add'}
              text={LanguageUtil.get({ id: 'common.button.create', defaultMessage: 'Create' })}
              customType={'primary'}
              className={'select-none'}
              onClick={() => RoleHelper.canEdit(props.type) && open()}
            />
          )}
        </section>
      </>
    );
  };

  return (
    <div className={'flex flex-col p-4 items-center justify-center w-full h-full bg-main-gray border-[1px] gap-5 border-[#E0E4E8] rounded-[10px]'}>
      <div className={'flex items-center w-40 h-40 justify-center rounded-full bg-secondary'}>
        <section className={'flex items-center w-32 h-32 justify-center rounded-full bg-white drop-shadow'}>
          <Icon icon={getComponentIcon(type)} className={'text-[#728EFF]'} size={45} />
        </section>
      </div>
      <section className={'flex flex-col items-center justify-center gap-y-2 select-none'}>
        <h5 className={'text-black 2xl'}>
          {LanguageUtil.get(
            {
              id: 'components.emptyComponent.notCreatedYet',
              defaultMessage: `There is no ${getType()} created yet.`,
            },
            { 0: `${getType()}` },
          )}
        </h5>
      </section>
      <section className={'flex flex-col items-center justify-center text-sm select-none'}>
        <p className={'text-f-black-3'}>
          {LanguageUtil.get({ id: 'components.emptyComponent.description', defaultMessage: 'You can authorize users and groups to access flow here.' })}
        </p>
        <p className={'text-f-black-3'}>
          {LanguageUtil.get({ id: 'components.emptyComponent.description2', defaultMessage: 'For more information, please read the' })}
          <a className={'underline cursor-pointer text-[#488EDA]'}>
            {LanguageUtil.get({ id: 'components.emptyComponent.accessManagementPolicy', defaultMessage: 'Access Management Policy' })}
          </a>
        </p>
      </section>
      {getCreateList()}
    </div>
  );
}

interface Props extends FormicaProps {
  type: ComponentName;
  createDisabled?: boolean;
  onCreate?: () => void;
}

export default withFormica(EmptyComponent);
